import { useInboxAgentQuery } from "@qw/graphql";
import { scopedAgentId, useInboxScope } from "@/providers/inbox-scope";

/**
 * Who the agent scope is, above their mail.
 *
 * **This is the unit that serves the observed shadowing story.** A producer hunting an agent's
 * phone number today searches for their email and scrolls to the bottom of a thread hoping for a
 * signature. The CRM contact directory has a number for about half of them, and this is the
 * surface that can spend it.
 *
 * Every field beside the name renders only where it exists — no placeholder, no "not available"
 * row. `AgentContact` in the API carries the coverage this is sized for; the short version is that
 * a card announcing the missing half as blanks would be mostly blanks.
 *
 * The read is by id and is not fenced to the caller's mail, so the card names the agent in cases
 * where the chip beside the search box still cannot: `useBookScope` reads its name off the
 * broker's own book, and an agent with no open account there has none to read.
 */
export const AgentCard = () => {
  const scope = useInboxScope();
  const agentId = scopedAgentId(scope);
  const { data } = useInboxAgentQuery({ variables: { agentId: agentId ?? "" }, skip: !agentId });

  // The route is what decides whether there is a card, not the query. Apollo returns no data under
  // `skip`, so `agentId` reads as redundant here — but that would make "no card outside an agent
  // scope" a property of Apollo's skip semantics rather than of this component, and it is the
  // second of those the inbox relies on. It is also what keeps the placeholder variable unread.
  //
  // Past that: nothing while it loads, nothing when the id names no agent, and nothing on failure.
  // All three are the same to a broker, and a card is context for the list rather than the list
  // itself — an error banner here would put a failed phone lookup on top of mail that loaded fine.
  if (!agentId || !data?.inboxAgent) {
    return null;
  }

  const agent = data.inboxAgent;
  const name = `${agent.firstName} ${agent.lastName}`.trim();

  return (
    // A card that floats in the row rather than another band across it. The mail list below is a
    // stack of full-bleed rows, and a full-bleed card reads as the first of them — the inset and
    // the shadow are what say this is about the list rather than in it.
    <section aria-label="Agent contact" className="flex-none px-3 py-2 text-sm">
      <div className="bg-card border flex gap-4 items-start justify-between px-3 py-2 rounded-lg shadow-sm">
        <div className="flex flex-col min-w-0">
          {/* `name` in `useBookScope` can be an empty string for the same reason — the columns are
              NOT NULL but not non-blank. The email is the one thing every account has. */}
          <span className="font-medium truncate">{name || agent.email}</span>
          <span className="text-muted-foreground text-xs truncate">{agent.agencyName}</span>
        </div>
        {/* A description list so a screen reader reads each label with its value, and each row
            renders only where the directory has one, so the column collapses rather than growing
            an empty row. The email is dropped when it is already standing in for the name above —
            the card would otherwise say it twice. */}
        <dl className="flex flex-col gap-y-0.5 items-end min-w-0 text-xs">
          {name && <Field label="Email" value={agent.email} />}
          {agent.mobilePhone && <Field label="Mobile" value={agent.mobilePhone} />}
          {agent.workPhone && <Field label="Work" value={agent.workPhone} />}
        </dl>
      </div>
    </section>
  );
};

/**
 * One labelled contact value, rendered exactly as the directory stores it. Numbers arrive already
 * formatted for a human — `(512) 354-7177`, `(972) 250-1600 ext. 240` — and reformatting them here
 * would only invent a second spelling of a number a broker is about to dial.
 */
const Field = ({ label, value }: { label: string; value: string }) => (
  <div className="flex gap-1.5 items-baseline min-w-0">
    <dt className="text-muted-foreground">{label}</dt>
    <dd className="truncate">{value}</dd>
  </div>
);
